import { useCallback, useEffect, useState } from 'react';
import { Eye, Plus, Trash2 } from 'lucide-react';
import { apiInternalAffaires, apiAffaires, apiAffaireTeam } from '../api/client.js';
import { affaireLevel } from '../lib/affaireAccess.js';
import Modal from '../components/Modal.jsx';
import DatePicker from '../components/DatePicker.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';
import PeoplePicker from '../components/PeoplePicker.jsx';
import Avatar from '../components/Avatar.jsx';
import InternalAffaireBadge from '../components/InternalAffaireBadge.jsx';

/**
 * Ticket #54 — « Affaires internes » : un MANAGER crée des affaires internes /
 * temporaires (réunions, R&D, formation…) sur lesquelles son équipe impute du
 * temps NON facturable, jamais exporté vers Gescom.
 *
 * Volontairement distincte de `AffairesView` : aucun champ client, budget,
 * TJM, statut ni référence Gescom — le serveur les force
 * (`/api/affaires/internal`).
 *
 * #108 — la liste couvre TOUTES les affaires internes du périmètre : celles du
 * client de rattachement (SYNOTIS CH SA : Intercontrat, Formation, RH…) ET
 * celles créées ici (`isInternal`). Le niveau d'accès (`access.level`) pilote
 * les actions : `read` = consultation, `delegated` = modification. Les
 * premières (sans `isInternal`, souvent liées à Gescom) se modifient par la
 * route générique `PATCH /api/affaires/:id` ; ⚠ elles ne reçoivent jamais
 * `isInternal`, qui les sortirait de l'export Gescom et de l'API publique.
 *
 * Qui peut imputer : le créateur (project manager de l'affaire) et les membres
 * désignés dans l'onglet Équipe — toute personne active, y compris hors de
 * sa hiérarchie.
 */

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: 'var(--card-bg)',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const iconBtn = {
  background: 'transparent',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const labelStyle = { display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 };

const th = { padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 };
const td = { padding: '10px 12px', color: 'var(--text-muted)' };

const EMPTY_FORM = { name: '', notes: '', startDate: '', endDate: '', expiresAt: '' };

function toDateInputValue(iso) {
  return iso ? String(iso).slice(0, 10) : '';
}

function formatDay(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function InternalAffairesView({ user }) {
  const isAdmin = user?.role === 'ADMIN';
  // #54 — création réservée aux managers (et ADMIN).
  const canCreate = isAdmin || user?.role === 'MANAGER';
  const [archivedFilter, setArchivedFilter] = useState('active');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('info');
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: rows } = await apiInternalAffaires.list(archivedFilter);
      setItems(rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setTab('info');
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setTab('info');
    setForm({
      name: a.name ?? '',
      notes: a.notes ?? '',
      startDate: toDateInputValue(a.startDate),
      endDate: toDateInputValue(a.endDate),
      expiresAt: toDateInputValue(a.expiresAt),
    });
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    // Le nombre de membres affiché dans la liste a pu changer (onglet Équipe).
    if (editing) reload();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      // Payload strict : le serveur refuse tout autre champ (400).
      const payload = {
        name: form.name.trim(),
        notes: form.notes.trim() || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        expiresAt: form.expiresAt || null,
      };
      if (editing) {
        if (editing.isInternal) {
          await apiInternalAffaires.update(editing.id, payload);
        } else {
          // Affaire du client de rattachement : route générique, sans
          // `expiresAt` (propre aux affaires créées ici).
          const { expiresAt: _ignored, ...generic } = payload;
          await apiAffaires.update(editing.id, generic);
        }
        setModalOpen(false);
        await reload();
      } else {
        const created = await apiInternalAffaires.create(payload);
        await reload();
        // Enchaîne sur l'équipe : sans membre, seul le créateur peut imputer.
        setEditing({ ...created, activeTeamCount: 0 });
        setTab('team');
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (a) => {
    if (!window.confirm(`Archiver l'affaire interne "${a.name}" ?`)) return;
    try {
      await (a.isInternal ? apiInternalAffaires.archive(a.id) : apiAffaires.archive(a.id));
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const onRestore = async (a) => {
    try {
      await (a.isInternal ? apiInternalAffaires.restore(a.id) : apiAffaires.restore(a.id));
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const today = todayIso();
  // #108 — consultation seule au niveau `read` ; l'expiration n'existe que sur
  // les affaires créées ici (`isInternal`).
  const readOnly = Boolean(editing) && affaireLevel(editing, isAdmin) === 'read';
  const showExpiry = !editing || Boolean(editing.isInternal);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Affaires internes</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        {canCreate ? (
          <button
            type="button"
            onClick={openCreate}
            style={{ ...primaryBtn, marginLeft: 'auto', flexShrink: 0 }}
          >
            <Plus size={14} /> Nouvelle affaire interne
          </button>
        ) : null}
      </div>

      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>
        Affaires du client interne et affaires temporaires créées par les managers.{' '}
        {isAdmin
          ? 'En tant qu’administrateur, vous les voyez toutes.'
          : 'Vous voyez celles dont vous êtes responsable ou sur lesquelles vous intervenez.'}
      </p>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'auto',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={th}>Nom</th>
              <th style={th}>Code</th>
              <th style={th}>Période</th>
              <th style={th}>Expiration</th>
              <th style={th}>Équipe</th>
              <th style={th}>Responsable</th>
              <th style={{ ...th, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Chargement…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Aucune affaire interne.
                </td>
              </tr>
            ) : (
              items.map((a) => {
                const expiry = toDateInputValue(a.expiresAt);
                const expired = Boolean(expiry) && expiry < today;
                return (
                  <tr key={a.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          title={affaireLevel(a, isAdmin) === 'read' ? 'Consulter' : "Modifier l'affaire interne"}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            color: 'var(--accent)',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {a.name}
                        </button>
                        {/* Pastille réservée aux affaires créées ici (#54). */}
                        {a.isInternal ? <InternalAffaireBadge expiresAt={a.expiresAt} /> : null}
                        {a.archivedAt ? (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(archivée)</span>
                        ) : null}
                      </span>
                    </td>
                    <td style={td}>{a.code ?? '—'}</td>
                    <td style={td}>
                      {a.startDate || a.endDate
                        ? `${formatDay(a.startDate)} → ${formatDay(a.endDate)}`
                        : '—'}
                    </td>
                    <td style={{ ...td, color: expired ? 'var(--danger)' : 'var(--text-muted)' }}>
                      {expiry ? `${formatDay(expiry)}${expired ? ' (expirée)' : ''}` : '—'}
                    </td>
                    <td style={td}>{a.activeTeamCount ?? 0}</td>
                    <td style={td}>
                      {a.projectManager
                        ? `${a.projectManager.firstName} ${a.projectManager.lastName}`
                        : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {affaireLevel(a, isAdmin) === 'read' ? (
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          title="Consulter"
                          aria-label="Consulter"
                          style={{ ...iconBtn, color: 'var(--text)' }}
                        >
                          <Eye size={14} />
                        </button>
                      ) : (
                        <EntityActions
                          archived={Boolean(a.archivedAt)}
                          onEdit={() => openEdit(a)}
                          onArchive={() => onArchive(a)}
                          onRestore={() => onRestore(a)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? `Affaire interne — ${editing.name}` : 'Nouvelle affaire interne'}
        subtitle={
          !editing || editing.isInternal
            ? 'Temps non facturable, jamais exporté vers Gescom'
            : editing.client?.name ?? undefined
        }
        footer={
          tab === 'info' && !readOnly ? (
            <>
              <button type="button" onClick={closeModal} style={secondaryBtn}>
                Annuler
              </button>
              <button
                type="submit"
                form="internal-affaire-form"
                disabled={submitting || !form.name.trim()}
                style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
              >
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </>
          ) : (
            <button type="button" onClick={closeModal} style={secondaryBtn}>
              Fermer
            </button>
          )
        }
      >
        {editing ? (
          <div style={{ display: 'inline-flex', gap: 6, marginBottom: 16 }}>
            {[
              { key: 'info', label: 'Informations' },
              { key: 'team', label: 'Équipe' },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                aria-pressed={tab === t.key}
                style={{
                  padding: '4px 12px',
                  borderRadius: 999,
                  border: '1px solid var(--glass-border)',
                  background: tab === t.key ? 'var(--primary)' : 'var(--glass)',
                  color: tab === t.key ? 'var(--card-bg)' : 'var(--text)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        ) : null}

        {tab === 'info' ? (
          <form id="internal-affaire-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 14 }}>
            <fieldset
              disabled={readOnly}
              style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'grid', gap: 14 }}
            >
            <div>
              <label htmlFor="internal-affaire-name" style={labelStyle}>
                Nom *
              </label>
              <input
                id="internal-affaire-name"
                value={form.name}
                maxLength={255}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex. Réunions d'équipe, Formation React…"
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="internal-affaire-notes" style={labelStyle}>
                Description
              </label>
              <textarea
                id="internal-affaire-notes"
                value={form.notes}
                rows={3}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div>
                <span style={labelStyle}>Début</span>
                <DatePicker
                  value={form.startDate}
                  onChange={(v) => setForm({ ...form, startDate: v })}
                  ariaLabel="Date de début"
                  style={inputStyle}
                />
              </div>
              <div>
                <span style={labelStyle}>Fin</span>
                <DatePicker
                  value={form.endDate}
                  onChange={(v) => setForm({ ...form, endDate: v })}
                  ariaLabel="Date de fin"
                  style={inputStyle}
                />
              </div>
              {showExpiry ? (
              <div>
                <span style={labelStyle}>Expiration</span>
                <DatePicker
                  value={form.expiresAt}
                  onChange={(v) => setForm({ ...form, expiresAt: v })}
                  ariaLabel="Date d'expiration"
                  style={inputStyle}
                />
              </div>
              ) : null}
            </div>
            {showExpiry ? (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Expiration : aucune saisie ne sera acceptée au-delà de cette date (facultatif).
              </span>
            ) : null}
            {!editing ? (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Après la création, désignez dans l’onglet Équipe les collaborateurs autorisés à imputer.
              </span>
            ) : null}
            </fieldset>
            {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
          </form>
        ) : (
          <InternalAffaireTeam
            affaire={editing}
            readOnly={readOnly || Boolean(editing?.archivedAt)}
            archived={Boolean(editing?.archivedAt)}
          />
        )}
      </Modal>
    </div>
  );
}

/**
 * Équipe autorisée à imputer. Réutilise les routes `/api/affaires/:id/team`
 * (#108 : ouvertes au niveau délégué). Pas d'overrides de taux : sans objet
 * sur du temps non facturable.
 */
function InternalAffaireTeam({ affaire, readOnly, archived }) {
  const affaireId = affaire?.id;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!affaireId) return;
    setLoading(true);
    setError(null);
    try {
      const { items } = await apiAffaireTeam.list(affaireId, 'active');
      setMembers(items);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [affaireId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const fetchOptions = useCallback(
    async (q) => {
      try {
        const { items } = await apiAffaireTeam.available(affaireId, q, 20);
        return items;
      } catch {
        return [];
      }
    },
    [affaireId],
  );

  const onAdd = async (option) => {
    if (!option) return;
    setError(null);
    try {
      await apiAffaireTeam.create(affaireId, { personId: option.id });
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const onRemove = async (m) => {
    if (!window.confirm(`Retirer ${m.fullName} ? Cette personne ne pourra plus imputer sur l'affaire.`)) return;
    setError(null);
    try {
      await apiAffaireTeam.remove(affaireId, m.id);
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
        {affaire?.projectManager
          ? `${affaire.projectManager.firstName} ${affaire.projectManager.lastName} (responsable) peut toujours imputer. `
          : ''}
        Ajoutez ici les personnes autorisées à imputer, qu’elles soient ou non dans votre équipe.
      </p>
      {archived ? (
        <div style={{ fontSize: 13, color: 'var(--warning)' }}>
          Affaire archivée : restaurez-la pour modifier son équipe.
        </div>
      ) : readOnly ? null : (
        <PeoplePicker
          fetchOptions={fetchOptions}
          onSelect={onAdd}
          placeholder="Ajouter une personne…"
          emptyMessage="Aucune personne disponible"
        />
      )}
      {error ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div> : null}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</div>
      ) : members.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Aucun membre désigné.</div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {members.map((m) => (
            <li
              key={m.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 8px',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <Avatar person={{ fullName: m.fullName }} size={26} />
              <span style={{ flex: 1, minWidth: 0, color: 'var(--text)', fontSize: 14 }}>
                {m.fullName}
                {m.jobTitle ? (
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> — {m.jobTitle}</span>
                ) : null}
              </span>
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => onRemove(m)}
                  title="Retirer de l'équipe"
                  aria-label={`Retirer ${m.fullName} de l'équipe`}
                  style={{ ...iconBtn, color: 'var(--danger)' }}
                >
                  <Trash2 size={14} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
